<template>
  <UIInputSelect
    class="PreviewHtmlSelector"
    name="previewHtmlBlock"
    :items="options"
    :modelValue="selectedKey"
    :isDisabled="isDisabled"
    @update:modelValue="onChange"
  >
    <template #option="{ option }">
      <span class="PreviewHtmlSelector__option">
        <span class="PreviewHtmlSelector__option-text">
          <span class="PreviewHtmlSelector__option-title">
            {{ option.label }}
          </span>
          <span class="PreviewHtmlSelector__option-status">
            {{ statusForKey(option.value) }}
          </span>
        </span>
      </span>
    </template>
  </UIInputSelect>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { UIInputSelect } from '@foundever/ui-lib'

import type { ChatHtmlPreviewBlock } from '@/composables/useHtmlPreviewPublish'
import { formatDate } from '@/utils/formatters'

type PreviewHtmlOption = { label: string; value: string }

const props = defineProps<{
  blocks: ChatHtmlPreviewBlock[]
  /** Key of the previewed block: `${messageId}:${blockIndex}`. */
  selectedKey: string
  isDisabled?: boolean
}>()

const emit = defineEmits<{
  select: [block: ChatHtmlPreviewBlock]
}>()

/** Composite key — `blockIndex` is per-message, so it alone is not unique. */
function blockKey(block: ChatHtmlPreviewBlock): string {
  return `${block.messageId}:${block.blockIndex}`
}

/** File-style label from the page title (`Sales` → `Sales.html`), disambiguating
 * duplicate titles (`Sales_2.html`). */
const options = computed<PreviewHtmlOption[]>(() => {
  const nameCounts = new Map<string, number>()
  return props.blocks.map((block) => {
    const base = (block.title || 'Untitled page').trim()
    const seen = nameCounts.get(base) ?? 0
    nameCounts.set(base, seen + 1)
    const name = seen === 0 ? base : `${base}_${seen + 1}`
    return { label: `${name}.html`, value: blockKey(block) }
  })
})

function statusForKey(value: string): string {
  const block = props.blocks.find((entry) => blockKey(entry) === value)
  if (!block?.isPublished) return 'Not published'
  const ts = block.publishedAt
  if (!ts) return 'Published'
  // Website timestamps are unix seconds; guard in case one arrives in ms.
  const ms = ts < 1e12 ? ts * 1000 : ts
  return `Published ${formatDate(ms, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })}`
}

function onChange(value: string) {
  const block = props.blocks.find((entry) => blockKey(entry) === value)
  if (block) emit('select', block)
}
</script>

<style lang="scss" scoped>
.PreviewHtmlSelector {
  min-width: 0;
  max-width: 18rem;

  // ui-lib forces the trigger/toggle to width:100% via a high-specificity internal
  // rule that outranks normal :deep selectors — override so it sizes to content.
  :deep(.UIDropdown__trigger),
  :deep(.UIDropdownToggle) {
    width: auto !important;
    border: none;
  }
}
</style>

<style lang="scss">
// The dropdown menu is teleported to <body>, so option styles cannot be scoped.
.PreviewHtmlSelector__option {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;

  .PreviewHtmlSelector__option-text {
    display: flex;
    flex-direction: column;
    gap: var(---fds-global-space-xxxs);
    min-width: 0;
  }

  .PreviewHtmlSelector__option-title {
    @include font-body-small-medium;
    color: var(--text-primary, #12123f);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .PreviewHtmlSelector__option-status {
    @include font-caption-regular;
    color: var(--text-secondary, #57576b);
  }
}
</style>
